import { useRef, useMemo, useEffect } from 'react';

import { debounce } from '../utils/elementSettingsUtils';
import { ChildElement } from '../types/elementSettingsTypes';
import { ElementProps } from '../../../../../@types/forms/types';
import { getCustomLists } from '../../../../../services/formService';
import { DEBOUNCE_DELAY } from '../constants/elementSettingsConstants';
import { ElementSettingsActionType } from '../types/elementSettingsTypes';
import { FormElementType } from '../../../../../@types/forms/enums/FormElementType';
import {
  getTextStyle,
  getTextDecoration,
  getTextStyleFlags,
  getTextDecorationFlag,
} from '../utils/elementSettingsUtils';

/**
 * Custom hook for syncing element state with parent and external data
 * Fixes the useEffect cascade problem by consolidating sync logic
 */
export const useElementSync = (
  element: ElementProps | null,
  state: any,
  dispatch: any,
  handleChange: (element: ElementProps) => void,
  elements: ElementProps[],
  eventTypesList: any[],
  open: boolean
) => {
  const isInitialMount = useRef(true);
  const previousElementId = useRef<string | null>(null);
  const previousOpen = useRef<boolean>(false);

  // Debounced change handler to prevent excessive parent updates
  const debouncedHandleChange = useMemo(
    () =>
      debounce((updatedElement: ElementProps) => {
        handleChange(updatedElement);
      }, DEBOUNCE_DELAY),
    [handleChange]
  );

  // Initialize state when dialog opens or element changes
  useEffect(() => {
    // Track dialog open/close state
    const dialogJustOpened = open && !previousOpen.current;
    const dialogJustClosed = !open && previousOpen.current;
    previousOpen.current = open;

    // Flush pending changes when dialog is closed
    if (dialogJustClosed) {
      debouncedHandleChange.flush();
      previousElementId.current = null;
      isInitialMount.current = true;
      return;
    }

    // Don't initialize if dialog is not open
    if (!open || !element) {
      return;
    }

    // Initialize state when:
    // 1. Dialog just opened (even with same element)
    // 2. Element ID changed
    const shouldInitialize =
      dialogJustOpened ||
      (element && element.id !== previousElementId.current);

    if (shouldInitialize) {
      previousElementId.current = element.id;

      dispatch({ type: ElementSettingsActionType.INITIALIZE_FROM_ELEMENT, payload: element });

      // Initialize text style flags
      if (element.textStyle) {
        const textStyleFlags = getTextStyleFlags(element.textStyle);
        dispatch({ type: ElementSettingsActionType.SET_TEXT_STYLE, payload: textStyleFlags });
      } else {
        dispatch({ type: ElementSettingsActionType.SET_TEXT_STYLE, payload: null });
      }

      // Initialize text decoration flags
      if (element.textDecoration !== undefined) {
        const textDecorationFlag = getTextDecorationFlag(element.textDecoration);
        dispatch({ type: ElementSettingsActionType.SET_TEXT_DECORATION, payload: textDecorationFlag });
      } else {
        dispatch({ type: ElementSettingsActionType.SET_TEXT_DECORATION, payload: null });
      }
    }
  }, [open, element, dispatch, debouncedHandleChange]);

  // Sync selectedElement updates back to parent (debounced)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (state.selectedElement) {
      debouncedHandleChange(state.selectedElement);
    }

    // No cleanup - we flush on dialog close instead
    // This prevents canceling pending changes when state updates rapidly
  }, [state.selectedElement, debouncedHandleChange]);

  // Sync text style changes to selectedElement
  useEffect(() => {
    if (state.elementTextStyle && state.selectedElement) {
      const textStyle = getTextStyle(state.elementTextStyle);
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { textStyle },
      });
    }
  }, [state.elementTextStyle, dispatch]);

  // Sync text decoration changes to selectedElement
  useEffect(() => {
    if (state.elementTextDecoration !== null && state.selectedElement) {
      const textDecoration = getTextDecoration(state.elementTextDecoration);
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { textDecoration },
      });
    }
  }, [state.elementTextDecoration, dispatch]);

  // Sync text rotation to selectedElement
  useEffect(() => {
    if (
      state.selectedElement &&
      typeof state.textRotation === 'number' &&
      state.selectedElement.rotate !== state.textRotation
    ) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { rotate: state.textRotation },
      });
    }
  }, [state.textRotation, dispatch]);

  // Sync color to selectedElement
  useEffect(() => {
    if (state.selectedElement && state.selectedColor) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { color: state.selectedColor },
      });
    }
  }, [state.selectedColor, dispatch]);

  // Sync options to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { options: state.options },
      });
    }
  }, [state.options, dispatch]);

  // Sync form group options to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { formGroupOptions: state.formGroupOptions },
      });
    }
  }, [state.formGroupOptions, dispatch]);

  // Sync uploaded file to selectedElement
  useEffect(() => {
    if (state.selectedElement && state.uploadedFile) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { file: state.uploadedFile },
      });
    }
  }, [state.uploadedFile, dispatch]);

  // Sync selected list to selectedElement
  useEffect(() => {
    if (state.selectedElement && state.selectedList) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { list: state.selectedList },
      });
    }
  }, [state.selectedList, dispatch]);

  // Sync static text to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { staticText: state.staticText },
      });
    }
  }, [state.staticText, dispatch]);

  // Sync days of country to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { daysOfCountry: state.daysOfCountry },
      });
    }
  }, [state.daysOfCountry, dispatch]);

  // Sync date range template to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { dateRangeTemplate: state.dateRangeTemplate },
      });
    }
  }, [state.dateRangeTemplate, dispatch]);

  // Sync calendar event type to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      const eventTypeId = state.calendarEventType ? state.calendarEventType.id : null;
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { calendarEventType: eventTypeId },
      });
    }
  }, [state.calendarEventType, dispatch]);

  // Sync required flag to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { required: state.isElementRequired },
      });
    }
  }, [state.isElementRequired, dispatch]);

  // Sync address book type to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: {
          isForExternal: state.isForExternal,
          isForInternal: state.isForInternal,
          isForAll: state.isForAll,
        },
      });
    }
  }, [state.isForExternal, state.isForInternal, state.isForAll, dispatch]);

  // Sync today date to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { todayDate: state.todayDate },
      });
    }
  }, [state.todayDate, dispatch]);

  // Sync past disabled to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { disablePast: state.isPastDisabled },
      });
    }
  }, [state.isPastDisabled, dispatch]);

  // Sync validation to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { validations: state.validation },
      });
    }
  }, [state.validation, dispatch]);

  // Sync hyperlink to selectedElement (only for Hyperlink elements)
  useEffect(() => {
    if (state.selectedElement && state.selectedElement.type === FormElementType.Hyperlink) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { hyperlink: state.hyperlink },
      });
    }
  }, [state.hyperlink, dispatch]);

  // Sync user info to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { userDataOption: state.userInfo },
      });
    }
  }, [state.userInfo, dispatch]);

  // Sync number of characters to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { numberOfCharacters: state.numberOfCharacters },
      });
    }
  }, [state.numberOfCharacters, dispatch]);

  // Sync child elements to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: {
          childElements: state.childElements,
          isParent: state.childElements.length > 0,
        },
      });
    }
  }, [state.childElements, dispatch]);

  // Sync prefix to selectedElement
  useEffect(() => {
    if (state.selectedElement) {
      dispatch({
        type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD,
        payload: { prefix: state.prefix },
      });
    }
  }, [state.prefix, dispatch]);

  // Fetch custom lists for CustomList element type
  useEffect(() => {
    if (state.selectedElement?.type === FormElementType.CustomList) {
      const fetchLists = async () => {
        try {
          const lists = await getCustomLists();
          dispatch({ type: ElementSettingsActionType.SET_CUSTOM_LISTS, payload: lists });
        } catch (error) {
          console.error('Failed to fetch custom lists:', error);
        }
      };
      fetchLists();
    }
  }, [state.selectedElement?.type, dispatch]);

  // Build same type elements list for child element selection
  useEffect(() => {
    if (state.selectedElement && elements) {
      const sameTypeElementsArr: ChildElement[] = [];

      elements.forEach((el) => {
        if (
          state.selectedElement.type === el.type &&
          el.id !== state.selectedElement.id &&
          !el.isChild &&
          !el.isParent
        ) {
          sameTypeElementsArr.push({ id: el.id, name: el.label });
        }
      });

      dispatch({
        type: ElementSettingsActionType.SET_SAME_TYPE_ELEMENTS_LIST,
        payload: sameTypeElementsArr,
      });
    }
  }, [state.selectedElement?.type, state.selectedElement?.id, elements, dispatch]);

  // Find and set calendar event type from list
  useEffect(() => {
    if (state.selectedElement?.calendarEventType && eventTypesList.length > 0) {
      const eventType = eventTypesList.find((et) => et.id === state.selectedElement.calendarEventType);
      if (eventType) {
        dispatch({ type: ElementSettingsActionType.SET_CALENDAR_EVENT_TYPE, payload: eventType });
      }
    }
  }, [state.selectedElement?.calendarEventType, eventTypesList, dispatch]);
};
